/**
 * Plain data describing a board's hit-test geometry: how a mouse position
 * (in pixels, local to the board) maps to a grid cell, both for the
 * division-based hit test used on mouse down/up (`mouseToCell`) and the
 * pixel-band hit test used while dragging (`onBoardMouseMove`).
 *
 * `cols` is intentionally NOT part of this shape: the block board is a fixed
 * 4x4 grid, but the rules boards' column count is dynamic (`intColumns`), so
 * engines read column count from `this.gridCols` rather than from geometry.
 *
 * Currently only the BLOCK board's two variants (AddSub/MulDiv) are modeled
 * here; the rules boards' geometry numbers still live in each rules game's
 * config hooks (`getMouseCoordinates`/`isPointOutOfBounds`/`computeHoverCell`)
 * and are unchanged by this step.
 */
export interface BoardGeometry {
  /** Number of grid rows (Block: 4, Rules: 3). */
  rows: number;
  /** Division-based hit test used by `getMouseCoordinates` (mouse down/up). */
  mouseToCell: {
    cellWidth: number;
    cellHeight: number;
    /** Block/AddSub: false. Block/MulDiv: true (clamp row/col to rows-1/cols-1). */
    capToGrid: boolean;
  };
  /** Pixel-band hit test used by `onBoardMouseMove` while dragging. */
  outOfBounds: { minX: number; maxX: number; minY: number; maxY: number };
  /** One pixel band per column, in column order. */
  colBands: ReadonlyArray<{ min: number; max: number }>;
  /** One pixel band per row, in row order. */
  rowBands: ReadonlyArray<{ min: number; max: number }>;
  /** Scale applied to the mobile piece on drag start (Block: 1.5, Rules: 2). */
  mobileScale: number;
}

/**
 * Shared block-board (4x4) geometry, extracted verbatim from
 * `BlockBoardEngine.getMouseCoordinates`/`onBoardMouseMove`. `capToGrid` is
 * the only field that differs between AddSub and MulDiv (AddSub: false,
 * MulDiv: true); everything else is identical between the two block games.
 */
function blockGeometry(capToGrid: boolean): BoardGeometry {
  return {
    rows: 4,
    mouseToCell: { cellWidth: 135, cellHeight: 145, capToGrid },
    outOfBounds: { minX: -10, maxX: 560, minY: 11, maxY: 555 },
    colBands: [
      { min: 25, max: 155 },
      { min: 165, max: 290 },
      { min: 295, max: 425 },
      { min: 425, max: 560 },
    ],
    rowBands: [
      { min: -50, max: 65 },
      { min: 77, max: 205 },
      { min: 210, max: 338 },
      { min: 345, max: 550 },
    ],
    mobileScale: 1.5,
  };
}

/** AddSub: `getMouseCoordinates` does not cap row/col. */
export const ADD_SUB_GEOMETRY: BoardGeometry = blockGeometry(false);

/** MulDiv: `getMouseCoordinates` caps row/col to a max of 3. */
export const MUL_DIV_GEOMETRY: BoardGeometry = blockGeometry(true);
